import { createContext, useContext, useState, type ReactNode } from 'react'

export type BoardTheme = 'classic' | 'tournament' | 'ocean'

export const boardThemes: { id: BoardTheme; name: string; className: string }[] = [
  { id: 'classic', name: 'Classic', className: 'board-classic' },
  { id: 'tournament', name: 'Tournament', className: 'board-tournament' },
  { id: 'ocean', name: 'Ocean', className: 'board-ocean' },
]

type Settings = {
  boardTheme: BoardTheme
  sound: boolean
  setBoardTheme: (theme: BoardTheme) => void
  setSound: (on: boolean) => void
}

const SettingsContext = createContext<Settings | null>(null)

/** Reads a per-browser preference; storage can be unavailable (private windows), so fall back quietly. */
function stored<T>(key: string, fallback: T): T {
  try {
    const value = localStorage.getItem(key)
    return value === null ? fallback : (JSON.parse(value) as T)
  } catch {
    return fallback
  }
}

function store(key: string, value: unknown) {
  try {
    localStorage.setItem(key, JSON.stringify(value))
  } catch {
    // Not being able to remember a preference is fine.
  }
}

export function SettingsProvider({ children }: { children: ReactNode }) {
  const [boardTheme, setTheme] = useState<BoardTheme>(() => stored('boardTheme', 'classic'))
  const [sound, setSoundState] = useState<boolean>(() => stored('sound', true))

  const value: Settings = {
    boardTheme,
    sound,
    setBoardTheme: (theme) => {
      setTheme(theme)
      store('boardTheme', theme)
    },
    setSound: (on) => {
      setSoundState(on)
      store('sound', on)
    },
  }
  return <SettingsContext.Provider value={value}>{children}</SettingsContext.Provider>
}

export function useSettings(): Settings {
  const settings = useContext(SettingsContext)
  if (!settings) throw new Error('useSettings needs a SettingsProvider')
  return settings
}

export function boardThemeClass(theme: BoardTheme): string {
  return boardThemes.find((t) => t.id === theme)?.className ?? 'board-classic'
}

/** Remembers a per-drill choice (orientation, round length, opening) in this browser. */
export function useStoredState<T>(key: string, fallback: T): [T, (value: T) => void] {
  const [value, setValue] = useState<T>(() => stored(key, fallback))
  return [
    value,
    (next) => {
      setValue(next)
      store(key, next)
    },
  ]
}
